import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { IoClose, IoSaveOutline, IoBarcodeOutline, IoCameraOutline } from "react-icons/io5";
import api from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { tapSuccess, tapError } from "@/utils/haptics";

export default function ProductFormPage() {
  const { productId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isDirettore, isTitolare } = useAuth();
  const isNew = !productId;

  // For direttore, force the store to their own — no dropdown allowed.
  const defaultStore = isDirettore
    ? user?.storeId
    : (params.get("store_id") || ALL_STORES[0]);

  const [form, setForm] = useState({
    name: params.get("name") || "",
    article_code: params.get("article_code") || "",
    barcode: params.get("barcode") || "",
    quantity: 0,
    price: params.get("price") || "",
    cost: params.get("cost") || "",
    notes: params.get("notes") || "",
    image_file_id: params.get("image_file_id") || "",
    image_url: params.get("image_url") || "",
    expiry_date: "",
    store_id: defaultStore,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [shelfOcrBusy, setShelfOcrBusy] = useState(false);
  const shelfFileRef = useRef(null);

  /** PLAN B — user shoots a photo of the Conad shelf tag; Gemini Vision extracts
   *  ALL fields at once (name, article_code, EAN, price). Cost is filled from
   *  listino if the extracted barcode/article_code matches an entry. */
  const handleShelfTagPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setShelfOcrBusy(true);
    const t = toast.loading("Leggo il cartellino…");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/ocr/shelf-tag", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        if (data.name && !f.name) { next.name = data.name; filled.push("nome"); }
        if (data.article_code && !f.article_code) { next.article_code = data.article_code; filled.push("codice articolo"); }
        if (data.barcode && !f.barcode) { next.barcode = data.barcode; filled.push("EAN"); }
        if (data.price != null && (f.price === "" || f.price == null)) { next.price = data.price; filled.push("prezzo"); }
        if (data.cost != null && (f.cost === "" || f.cost == null)) { next.cost = data.cost; filled.push("costo"); }
        return next;
      });
      toast.dismiss(t);
      if (filled.length) {
        tapSuccess();
        toast.success(`Compilato dal cartellino: ${filled.join(", ")}`);
      } else {
        toast.info("Nessun campo leggibile — riprova con foto più nitida");
      }
    } catch (err) {
      tapError();
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "OCR fallito");
    } finally {
      setShelfOcrBusy(false);
    }
  };

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/products/${productId}`);
        setForm({
          name: data.name || "",
          article_code: data.article_code || "",
          barcode: data.barcode || "",
          quantity: data.quantity ?? 0,
          price: data.price ?? "",
          cost: data.cost ?? "",
          notes: data.notes || "",
          image_file_id: data.image_file_id || "",
          image_url: data.image_url || "",
          expiry_date: data.expiry_date || "",
          store_id: data.store_id,
        });
      } catch {
        toast.error("Prodotto non trovato");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [productId, isNew, navigate]);

  // Safety-net for NEW products opened with a barcode/article_code in the URL:
  // if we still miss any of article_code/cost/price, re-fetch from lookup so the form
  // is fully populated even when the caller forgot to pass every field.
  useEffect(() => {
    if (!isNew) return;
    const seed = form.barcode || form.article_code;
    if (!seed) return;
    const missing = !form.article_code || form.cost === "" || form.price === "";
    if (!missing) return;
    prefillFromLookup(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Lookup by EAN or article code and prefill all matching product fields.
   *  Called on blur of the EAN or article_code inputs, and on scan detection. */
  const prefillFromLookup = async (code) => {
    const trimmed = (code || "").toString().trim();
    if (!trimmed) return;
    try {
      const { data } = await api.get(`/products/lookup/${encodeURIComponent(trimmed)}`);
      if (!data || !data.name) {
        toast.info("Articolo non trovato nel listino: compila i campi manualmente");
        return;
      }
      setForm((f) => ({
        ...f,
        name: f.name || data.name || "",
        article_code: f.article_code || data.article_code || "",
        barcode: f.barcode || data.barcode || "",
        cost: (f.cost === "" || f.cost == null) && data.cost != null ? data.cost : f.cost,
        price: (f.price === "" || f.price == null) && data.price != null ? data.price : f.price,
        notes: f.notes || data.notes || "",
        image_url: f.image_url || data.image_url || "",
        image_file_id: f.image_file_id || data.image_file_id || "",
      }));
      // Signal to the user whether the fill is complete or partial, and warn on fuzzy matches.
      const isFuzzy = typeof data.source === "string" && data.source.includes("listino-fuzzy");
      const missing = [];
      if (!data.article_code) missing.push("codice articolo");
      if (data.cost == null) missing.push("costo");
      if (data.price == null) missing.push("prezzo");
      if (isFuzzy && data.matched_listino_name) {
        toast.success(`Abbinato al listino: "${data.matched_listino_name}". Verifica prima di salvare.`, {
          duration: 5000,
        });
      } else if (missing.length === 0) {
        toast.success(`Compilato dal listino: ${data.name}`);
      } else {
        toast.success(`${data.name} — completa a mano: ${missing.join(", ")}`);
      }
    } catch { /* silent — user will fill manually */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    const qty = Number(form.quantity);
    if (!Number.isFinite(qty) || qty < 1) {
      toast.error("La quantità è obbligatoria (min. 1)");
      return;
    }
    if (!form.expiry_date) {
      toast.error("La data di scadenza è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: qty,
        price: form.price === "" ? null : Number(form.price),
        cost: form.cost === "" ? null : Number(form.cost),
      };
      if (isNew) {
        await api.post("/products", payload);
        tapSuccess();
        toast.success("Articolo creato");
      } else {
        await api.put(`/products/${productId}`, payload);
        tapSuccess();
        toast.success("Articolo aggiornato");
      }
      navigate(`/catalog/${payload.store_id}`);
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-frame">
        <div className="tf-header-simple">
          <button className="tf-close-btn" onClick={() => navigate(-1)} aria-label={t("cancel")}>
            <IoClose size={22} />
          </button>
          <div className="tf-header-title-center">…</div>
          <div style={{ minWidth: 44 }} />
        </div>
        <div className="tf-empty">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="mobile-frame" data-testid="product-form-page">
      <div className="tf-header-simple">
        <button data-testid="pf-close" className="tf-close-btn" onClick={() => navigate(`/catalog/${form.store_id || ""}`)} aria-label={t("cancel")}>
          <IoClose size={22} />
        </button>
        <div className="tf-header-title-center" data-testid="page-title">
          {isNew ? t("new_product") : t("edit_product")}
        </div>
        <div style={{ minWidth: 44 }} />
      </div>

      <form onSubmit={submit} className="tf-content">
        {isNew && (
          <>
            <input
              ref={shelfFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              data-testid="pf-shelf-tag-file"
              onChange={handleShelfTagPhoto}
            />
            <button
              type="button"
              data-testid="pf-shelf-tag-btn"
              className="tf-btn-primary"
              onClick={() => shelfFileRef.current?.click()}
              disabled={shelfOcrBusy}
              style={{ width: "100%", marginBottom: 14, justifyContent: "center" }}
            >
              <IoCameraOutline size={20} />
              {shelfOcrBusy ? "Lettura in corso…" : "Fotografa il cartellino dello scaffale"}
            </button>
          </>
        )}

        <div className="tf-field">
          <label className="tf-label">Punto vendita</label>
          {isDirettore ? (
            <input
              data-testid="pf-store-locked"
              className="tf-input"
              value={t(STORE_LABELS[form.store_id] || "")}
              readOnly
            />
          ) : (
            <select data-testid="pf-store" className="tf-select" value={form.store_id} onChange={(e) => set("store_id", e.target.value)}>
              {ALL_STORES.map((s) => <option key={s} value={s}>{t(STORE_LABELS[s])}</option>)}
            </select>
          )}
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("name")} *</label>
          <input data-testid="pf-name" className="tf-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Es. Uova Biologiche" />
        </div>

        <div className="tf-field">
          <label className="tf-label">Codice articolo</label>
          <input
            data-testid="pf-article-code"
            className="tf-input"
            value={form.article_code}
            onChange={(e) => set("article_code", e.target.value)}
            onBlur={(e) => prefillFromLookup(e.target.value)}
            placeholder="Es. 0096731.001"
          />
        </div>

        <div className="tf-field">
          <label className="tf-label">Codice EAN</label>
          <div className="tf-input-wrap">
            <input
              data-testid="pf-barcode"
              className="tf-input tf-input-with-icon-right"
              value={form.barcode}
              onChange={(e) => set("barcode", e.target.value)}
              onBlur={(e) => prefillFromLookup(e.target.value)}
              inputMode="numeric"
              placeholder="8001120450081"
              style={{ paddingRight: 60 }}
            />
            <button
              type="button"
              data-testid="pf-barcode-scan"
              onClick={() => setScannerOpen(true)}
              className="tf-icon-btn red"
              style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 40, height: 40 }}
              aria-label={t("scan_barcode")}
            >
              <IoBarcodeOutline size={20} />
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="tf-field">
            <label className="tf-label">{t("quantity")} *</label>
            <input
              data-testid="pf-quantity"
              className="tf-input"
              type="number"
              min={1}
              required
              value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              onFocus={(e) => { if (String(form.quantity) === "0") { set("quantity", ""); e.target.select(); } }}
              inputMode="numeric"
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">{t("expiry_date")} *</label>
            <ExpiryDateInput value={form.expiry_date} onChange={(v) => set("expiry_date", v)} testIdPrefix="pf-expiry" />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="tf-field">
            <label className="tf-label">{t("cost")} (€)</label>
            <input data-testid="pf-cost" className="tf-input" type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} inputMode="decimal" placeholder="0.00" />
          </div>
          <div className="tf-field">
            <label className="tf-label">{t("price")} (€)</label>
            <input data-testid="pf-price" className="tf-input" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} inputMode="decimal" placeholder="0.00" />
          </div>
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("notes")}</label>
          <textarea data-testid="pf-notes" className="tf-textarea" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>

        <button data-testid="pf-save" type="submit" className="tf-btn-primary" disabled={saving}>
          <IoSaveOutline size={18} />
          {saving ? t("saving") : t("save")}
        </button>
      </form>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => { set("barcode", code); setScannerOpen(false); prefillFromLookup(code); }}
      />
    </div>
  );
}
